import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import {
  Activity, Database, ShieldCheck, TrendingUp, Lock, BarChart3,
  Sparkles, ArrowRight, CheckCircle2, Users,
} from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg aluminum-bg flex items-center justify-center shadow-sm">
              <Activity className="w-6 h-6 text-aea-navy" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-outfit font-bold text-lg leading-none">AEA</div>
              <div className="text-slate-500 text-[10px] tracking-widest uppercase">Espacio de Datos</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login-btn">Iniciar sesión</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-aea-blue hover:bg-aea-blue-dk" data-testid="nav-register-btn">Solicitar alta</Button></Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden bg-aea-navy text-white">
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: 'radial-gradient(circle at 20% 25%, rgba(3,105,161,0.5) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(148,163,184,0.2) 0%, transparent 50%)'
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 border border-aea-blue/40 bg-aea-blue/10 rounded-full px-3 py-1 text-xs text-aea-silver-lt tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-aea-blue-lt" />
              INTELIGENCIA SECTORIAL DEL ALUMINIO
            </div>
            <h1 className="mt-6 font-outfit font-bold text-4xl lg:text-6xl leading-[1.05] tracking-tight">
              El Espacio de Datos<br/>
              de la industria<br/>
              <span className="text-aea-silver">española del aluminio</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Automatiza la recopilación mensual de indicadores del sector. Cada empresa asociada
              aporta sus datos y recibe, al instante, información de valor: evolución propia,
              comparativa con el sector y análisis con IA. Siempre agregado. Siempre anónimo.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-aea-blue hover:bg-aea-blue-dk h-12 px-6" data-testid="hero-register-btn">
                  Unirme como asociado <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="ghost" className="text-white hover:bg-white/10 h-12 px-6" data-testid="hero-login-btn">
                  Ya soy asociado
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: '3', l: 'Roles definidos' },
                { n: '10+', l: 'Indicadores base' },
                { n: 'k≥3', l: 'Anonimato garantizado' },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-aea-blue pl-3">
                  <div className="text-3xl font-outfit font-bold">{s.n}</div>
                  <div className="text-xs text-slate-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <div className="relative bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 animate-fade-in-up">
              <div className="text-xs text-aea-silver-lt uppercase tracking-widest mb-3">Panel Empresa · Ejemplo</div>
              <div className="space-y-3">
                {[
                  { k: 'Producción de aluminio', v: '1.240 t', delta: '+8.4% vs media', c: 'text-emerald-400' },
                  { k: 'Consumo energético', v: '384.500 kWh', delta: '-3.2% vs media', c: 'text-emerald-400' },
                  { k: 'Chatarra reciclada', v: '312 t', delta: '+1.1% vs media', c: 'text-emerald-400' },
                  { k: 'Exportaciones', v: '842.000 €', delta: '-5.9% vs media', c: 'text-amber-400' },
                ].map((r) => (
                  <div key={r.k} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0">
                    <div className="text-sm text-slate-300">{r.k}</div>
                    <div className="text-right">
                      <div className="text-sm font-mono text-white">{r.v}</div>
                      <div className={`text-[11px] ${r.c}`}>{r.delta}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-lg bg-aea-blue/20 border border-aea-blue/30 p-3 text-xs text-slate-200">
                <span className="text-aea-blue-lt font-semibold">Agente IA:</span> "Su consumo energético está 3.2% por debajo de la media sectorial. Buen indicador de eficiencia."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPIOS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs text-aea-blue font-semibold uppercase tracking-widest">Principios</div>
            <h2 className="font-outfit font-bold text-3xl lg:text-4xl mt-2">Sus datos, su propiedad. Siempre.</h2>
            <p className="text-slate-600 mt-4 text-lg">
              El Espacio de Datos AEA aplica el concepto europeo de espacios de datos: soberanía,
              agregación anónima y valor compartido para todas las empresas asociadas.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { i: Lock, t: 'Propiedad del dato', d: 'Cada empresa conserva la propiedad de sus datos. AEA los usa únicamente según el contrato de adhesión.' },
              { i: ShieldCheck, t: 'Anonimización obligatoria', d: 'Comparativas siempre agregadas con mínimo de 3 empresas (k-anonimato). Nunca se muestran datos individuales.' },
              { i: TrendingUp, t: 'Valor inmediato', d: 'Al aportar datos, la empresa recibe al momento: evolución propia, comparativas y alertas sectoriales.' },
              { i: Database, t: 'Automatización total', d: 'Se acabaron los Excel manuales. Suba el fichero y el sistema valida, almacena y calcula estadísticas.' },
              { i: BarChart3, t: 'Motor estadístico', d: 'Totales, medias, percentiles, evolución, ranking anonimizado y series temporales por indicador.' },
              { i: Sparkles, t: 'Agente IA integrado', d: 'Pregunte en lenguaje natural. El agente responde con sus datos y comparativas del sector, con las mismas reglas de privacidad.' },
            ].map((f) => (
              <div key={f.t} className="aea-card p-6 card-hover">
                <div className="w-11 h-11 rounded-lg bg-aea-blue/10 text-aea-blue flex items-center justify-center">
                  <f.i className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 font-outfit font-semibold text-lg">{f.t}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESO */}
      <section className="py-20 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs text-aea-blue font-semibold uppercase tracking-widest">El flujo</div>
          <h2 className="font-outfit font-bold text-3xl lg:text-4xl mt-2">Del Excel manual al espacio de datos inteligente</h2>
          <div className="grid md:grid-cols-4 gap-6 mt-10">
            {[
              { n: '01', t: 'Apertura mensual', d: 'AEA abre la campaña del periodo. Cada empresa recibe aviso.' },
              { n: '02', t: 'Carga de datos', d: 'La empresa sube Excel o CSV con los valores del mes.' },
              { n: '03', t: 'Validación automática', d: 'Formato, columnas, unidades, duplicados y valores anómalos.' },
              { n: '04', t: 'Valor inmediato', d: 'Dashboard actualizado con evolución y benchmark sectorial.' },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="text-aea-blue font-mono text-sm">{s.n}</div>
                <h3 className="mt-3 font-outfit font-semibold text-lg">{s.t}</h3>
                <p className="text-slate-600 text-sm mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-outfit font-bold text-3xl lg:text-4xl">Únase al Espacio de Datos AEA</h2>
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
            Aporte sus datos mensuales y desbloquee inteligencia sectorial en tiempo real.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/register"><Button size="lg" className="bg-aea-blue hover:bg-aea-blue-dk h-12 px-6" data-testid="cta-register-btn">Solicitar alta <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            <Link to="/login"><Button size="lg" variant="outline" className="h-12 px-6" data-testid="cta-login-btn">Acceder</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
